import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Space, Typography, message, Progress, Alert, Tag, Modal } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, BulbOutlined, RightOutlined } from '@ant-design/icons';
import { getFillBlanks, checkFillBlank, submitFillBlanks } from '../../api/learn';

const { Title, Text, Paragraph } = Typography;

const Step3FillBlank = ({ onComplete }) => {
  const [fillBlanks, setFillBlanks] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [answerStatus, setAnswerStatus] = useState({}); // 存储每道题的对错状态
  const [correctAnswers, setCorrectAnswers] = useState({}); // 存储正确答案
  const [showHints, setShowHints] = useState({});
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    loadFillBlanks();
  }, []);

  const loadFillBlanks = async () => {
    try {
      setLoading(true);
      const response = await getFillBlanks();
      if (response.code === 200) {
        setFillBlanks(response.data.fillBlanks);
      }
    } catch (error) {
      message.error('加载题目失败');
    } finally {
      setLoading(false);
    }
  };

  // 验证答案并进入下一题
  const handleNextQuestion = async () => {
    const question = fillBlanks[currentQuestion];
    const userAnswer = answers[question.id];

    if (!userAnswer || userAnswer.trim() === '') {
      message.warning('请填写答案');
      return;
    }

    try {
      setChecking(true);
      const response = await checkFillBlank(question.id, userAnswer);
      
      if (response.code === 200) {
        const isCorrect = response.data.is_correct;
        
        // 记录答案状态
        setAnswerStatus(prev => ({
          ...prev,
          [question.id]: isCorrect ? 'correct' : 'wrong'
        }));

        // 记录正确答案
        if (!isCorrect && response.data.correct_answer) {
          setCorrectAnswers(prev => ({
            ...prev,
            [question.id]: response.data.correct_answer
          }));
        }

        if (isCorrect) {
          message.success('回答正确!');
        } else {
          message.error(`回答错误！正确答案是: ${response.data.correct_answer}`);
        }

        // 如果是最后一题
        if (currentQuestion === fillBlanks.length - 1) {
          // 检查是否全部答对
          const allCorrect = fillBlanks.every(q => 
            q.id === question.id ? isCorrect : answerStatus[q.id] === 'correct'
          );

          if (allCorrect) {
            // 全部答对，提交完成
            await submitComplete();
          } else {
            // 显示结果弹窗
            setShowResult(true);
          }
        } else {
          // 进入下一题
          setTimeout(() => {
            setCurrentQuestion(currentQuestion + 1);
          }, 500);
        }
      }
    } catch (error) {
      message.error('验证失败');
    } finally {
      setChecking(false);
    }
  };

  // 提交完成
  const submitComplete = async () => {
    try {
      setLoading(true);
      const answersArray = Object.entries(answers).map(([fillblankId, userAnswer]) => ({
        fillblank_id: parseInt(fillblankId),
        user_answer: userAnswer
      }));

      const response = await submitFillBlanks(answersArray);
      
      if (response.code === 200) {
        message.success('恭喜！完形填空完成！');
        setTimeout(() => {
          onComplete();
        }, 1500);
      }
    } catch (error) {
      message.error('提交失败');
    } finally {
      setLoading(false);
    }
  };

  // 重新开始
  const handleRestart = () => {
    setShowResult(false);
    setCurrentQuestion(0);
    setAnswers({});
    setAnswerStatus({});
    setCorrectAnswers({});
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const toggleHint = (questionId) => {
    setShowHints({ ...showHints, [questionId]: !showHints[questionId] });
  };

  if (fillBlanks.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text>加载中...</Text>
      </div>
    );
  }

  const question = fillBlanks[currentQuestion];
  const progressPercent = Math.round(((currentQuestion + 1) / fillBlanks.length) * 100);
  const correctCount = Object.values(answerStatus).filter(s => s === 'correct').length;

  // 将文本中的___替换为输入框
  const renderContent = (text, questionId) => {
    const parts = text.split('___');
    const status = answerStatus[questionId];
    const userAnswer = answers[questionId] || '';
    
    let inputStyle = { 
      width: 120, 
      margin: '0 8px',
      display: 'inline-block',
      textAlign: 'center'
    };
    
    // 根据状态设置边框颜色
    if (status === 'correct') {
      inputStyle.borderColor = '#52c41a';
      inputStyle.borderWidth = 2;
    } else if (status === 'wrong') {
      inputStyle.borderColor = '#ff4d4f';
      inputStyle.borderWidth = 2;
    }

    return (
      <span>
        {parts.map((part, index) => (
          <span key={index}>
            {part}
            {index < parts.length - 1 && (
              <Input
                style={inputStyle}
                value={userAnswer}
                onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                onPressEnter={handleNextQuestion}
                placeholder="填写答案"
                disabled={answerStatus[questionId] === 'correct'}
                status={status === 'wrong' ? 'error' : undefined}
              />
            )}
          </span>
        ))}
      </span>
    );
  };

  // 统计结果
  const resultStats = fillBlanks.map(q => ({
    id: q.id,
    itemNumber: q.item_number,
    status: answerStatus[q.id],
    userAnswer: answers[q.id],
    correctAnswer: correctAnswers[q.id]
  }));

  return (
    <div className="step-fillblank">
      <div className="step-header">
        <Title level={3}>步骤三：完形填空</Title>
        <Text type="secondary">填写学习内容中的关键词，巩固记忆</Text>
      </div>

      <Progress 
        percent={progressPercent} 
        status="active"
        format={() => `第 ${currentQuestion + 1}/${fillBlanks.length} 题`}
        style={{ marginBottom: 24 }}
      />

      <Alert
        message="答题说明"
        description={
          <Space direction="vertical" size={0}>
            <Text>1. 在空白处填写正确的词语</Text>
            <Text>2. 点击【下一题】提交答案，系统自动判断对错</Text>
            <Text>3. 6题全部答对方可完成本步骤</Text>
          </Space>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card 
        className="question-card"
        title={
          <Space>
            <Text strong style={{ fontSize: 16 }}>题目 {currentQuestion + 1}</Text>
            <Text type="secondary">|</Text>
            <Text type="secondary">准则 {question.item_number}</Text>
            {answerStatus[question.id] === 'correct' && (
              <Tag color="success" icon={<CheckCircleOutlined />}>已答对</Tag>
            )}
            {answerStatus[question.id] === 'wrong' && (
              <Tag color="error" icon={<CloseCircleOutlined />}>回答错误</Tag>
            )}
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Paragraph style={{ fontSize: 16, lineHeight: 2.5 }}>
            {renderContent(question.content_text, question.id)}
          </Paragraph>

          {/* 如果回答错误，显示正确答案 */}
          {answerStatus[question.id] === 'wrong' && correctAnswers[question.id] && (
            <Alert
              message={`正确答案：${correctAnswers[question.id]}`}
              type="error"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}

          {question.hints && question.hints.length > 0 && (
            <div>
              <Button 
                type="link" 
                icon={<BulbOutlined />}
                onClick={() => toggleHint(question.id)}
              >
                {showHints[question.id] ? '隐藏提示' : '查看提示'}
              </Button>
              {showHints[question.id] && (
                <Alert
                  message="提示"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {question.hints.map((hint, index) => (
                        <li key={index}>{hint}</li>
                      ))}
                    </ul>
                  }
                  type="warning"
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
          )}
        </Space>
      </Card>

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Space size="large">
          <Button
            disabled={currentQuestion === 0}
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
          >
            上一题
          </Button>
          
          <Text>
            已答对: <Text strong style={{ color: correctCount === fillBlanks.length ? '#52c41a' : '#1890ff' }}>{correctCount}</Text>/{fillBlanks.length}
          </Text>
          
          <Button
            type="primary"
            onClick={handleNextQuestion}
            loading={checking}
            icon={currentQuestion < fillBlanks.length - 1 ? <RightOutlined /> : null}
          >
            {currentQuestion < fillBlanks.length - 1 ? '下一题' : '提交完成'}
          </Button>
        </Space>
      </div>

      {/* 题目导航 */}
      <Card size="small" style={{ marginTop: 24 }} title="题目导航">
        <Space wrap>
          {fillBlanks.map((q, index) => (
            <Button
              key={q.id}
              size="small"
              type={currentQuestion === index ? 'primary' : 'default'}
              onClick={() => setCurrentQuestion(index)}
              style={{ 
                minWidth: 40,
                borderColor: answerStatus[q.id] === 'correct' ? '#52c41a' : 
                             answerStatus[q.id] === 'wrong' ? '#ff4d4f' : undefined,
                color: answerStatus[q.id] === 'correct' ? '#52c41a' : undefined
              }}
              icon={answerStatus[q.id] === 'correct' ? <CheckCircleOutlined /> : 
                    answerStatus[q.id] === 'wrong' ? <CloseCircleOutlined /> : null}
            >
              {index + 1}
            </Button>
          ))}
        </Space>
      </Card>

      {/* 结果弹窗 */}
      <Modal
        title="答题结果"
        open={showResult}
        footer={[
          <Button key="restart" onClick={handleRestart}>
            重新开始
          </Button>,
          <Button key="continue" type="primary" onClick={() => setShowResult(false)}>
            继续答题
          </Button>
        ]}
        width={600}
      >
        <Alert
          message={`还有 ${fillBlanks.length - correctCount} 题未答对，请继续努力！`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <div style={{ maxHeight: 300, overflow: 'auto' }}>
          {resultStats.map((item, index) => (
            <div key={item.id} style={{ marginBottom: 8 }}>
              <Space>
                <Text strong>第{index + 1}题：</Text>
                {item.status === 'correct' ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>正确</Tag>
                ) : item.status === 'wrong' ? (
                  <Tag color="error" icon={<CloseCircleOutlined />}>错误</Tag>
                ) : (
                  <Tag>未作答</Tag>
                )}
                {item.status === 'wrong' && item.correctAnswer && (
                  <Text type="secondary">正确答案：{item.correctAnswer}</Text>
                )}
              </Space>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Step3FillBlank;
